<template>
  <div ref="boxComponent" class="container box_component">
    <!-- Título y botón de nueva competición -->
    <div class="header-title">
      <h2>{{ $t('judges.title') }}</h2>
    </div>

    <!-- Barra de búsqueda y filtros -->
    <div class="filter-bar">
      <input
          v-model="searchQuery"
          @input="currentPage = 1"
          type="text"
          :placeholder="$t('judges.search_placeholder')"
          class="search-input"
      />
      <select
          v-model="selectedCountry"
          @change="onFilterChange"
          class="country-select"
      >
        <option value="">{{ $t('judges.filters.all_countries') }}</option>
        <option
            v-for="country in availableCountries"
            :key="country.code"
            :value="country.code"
        >
          {{ country.flag ? country.flag + ' ' : '' }}{{ country.code }}
        </option>
      </select>
      <select
          v-model="selectedSort"
          @change="onFilterChange"
          class="country-select"
      >
        <option value="default">{{ $t('judges.filters.sort.default') }}</option>
        <option value="competitions_desc">{{ $t('judges.filters.sort.competitions_desc') }}</option>
        <option value="competitions_asc">{{ $t('judges.filters.sort.competitions_asc') }}</option>
        <option value="rounds_desc">{{ $t('judges.filters.sort.rounds_desc') }}</option>
        <option value="rounds_asc">{{ $t('judges.filters.sort.rounds_asc') }}</option>
      </select>
      <select
          v-model="selectedCertification"
          @change="onFilterChange"
          class="country-select"
      >
        <option value="all">{{ $t('judges.filters.certification.all') }}</option>
        <option value="none">{{ $t('judges.filters.certification.none') }}</option>
        <option value="live">{{ $t('judges.filters.certification.live') }}</option>
        <option value="online">{{ $t('judges.filters.certification.online') }}</option>
      </select>
    </div>
    <!-- 🔹 VISTA DE ATLETAS -->
    <template v-if="this.judges.length > 0">
      <!-- Grid -->
      <div :class="['judge-grid', filteredAndPaginatedJudges.length <= 2 ? 'single' : '']">
        <JudgeListCard
            v-for="judge in filteredAndPaginatedJudges"
            :key="judge.id"
            :judge="judge"
            @viewJudge="handleViewJudge"
        />
      </div>
      <div class="pagination-controls" v-if="totalPages > 1">
        <button class="pagination-btn" @click="currentPage--" :disabled="currentPage === 1">
          {{ $t('judges.pagination.prev') }}
        </button>

        <span class="pagination-text">
          {{ $t('judges.pagination.page') }} {{ currentPage }} {{ $t('judges.pagination.of') }} {{ totalPages }}
        </span>

        <button class="pagination-btn" @click="currentPage++" :disabled="currentPage === totalPages">
          {{ $t('judges.pagination.next') }}
        </button>
      </div>
    </template>

    <p v-else class="no-judges">{{ $t("judges.empty_judges") }}</p>
  </div>
    <loading-modal ref="loadingModal"></loading-modal>
</template>

<script>
import LoadingModal from "@/services/LoadingModal.vue";
import {mapActions, mapState} from "vuex";
import router from "@/router/router";
import JudgeListCard from "@/components/judges/JudgeListCard.vue";
import {resolveResponsiveGridItemsPerPage} from "@/helpers/responsiveGridPagination";

export default {
  name: "JudgesList",
  components: {LoadingModal,JudgeListCard },
  data() {
    return {
      judges:[],
      searchQuery: "",
      selectedCountry: "",
      selectedSort: "default",
      selectedCertification: "all",
      isLoading: false,
      currentPage: 1,
      itemsPerPage: 12,
      resizeObserver: null,
      resizeFrame: null,
      observedContainerWidth: 0,
    };
  },
  watch: {
    searchQuery() {
      this.currentPage = 1;
    },
    selectedCountry() {
      this.currentPage = 1;
    },
    selectedSort() {
      this.currentPage = 1;
    },
    selectedCertification() {
      this.currentPage = 1;
    }
  },
  computed: {
    ...mapState('user', ['isAuthenticated','user','token']),
    ...mapState("competition",["appNavigation"]),
    filteredJudges() {
      const query = this.searchQuery.toLowerCase();

      return this.judges.filter(judge => {
        const matchesSearch = (
            judge.name?.toLowerCase().includes(query) ||
            judge.surname?.toLowerCase().includes(query)
        );
        const matchesCountry = !this.selectedCountry || this.getJudgeCountryCode(judge) === this.selectedCountry;
        const matchesCertification = this.selectedCertification === 'all'
          ? true
          : this.resolveJudgeCertificationType(judge) === this.selectedCertification;

        return matchesSearch && matchesCountry && matchesCertification;
      });
    },
    sortedJudges() {
      const list = [...this.filteredJudges];

      if (this.selectedSort === 'default') {
        return list;
      }

      return list.sort((left, right) => {
        if (this.selectedSort === 'competitions_desc') {
          return this.compareNumbers(this.resolveCompetitionsCount(right), this.resolveCompetitionsCount(left));
        }

        if (this.selectedSort === 'competitions_asc') {
          return this.compareNumbers(this.resolveCompetitionsCount(left), this.resolveCompetitionsCount(right));
        }

        if (this.selectedSort === 'rounds_desc') {
          return this.compareNumbers(this.resolveRoundsCount(right), this.resolveRoundsCount(left));
        }

        if (this.selectedSort === 'rounds_asc') {
          return this.compareNumbers(this.resolveRoundsCount(left), this.resolveRoundsCount(right));
        }

        return 0;
      });
    },
    filteredAndPaginatedJudges() {
      const start = (this.currentPage - 1) * this.itemsPerPage;
      return this.sortedJudges.slice(start, start + this.itemsPerPage);
    },

    totalPages() {
      return Math.ceil(this.sortedJudges.length / this.itemsPerPage);
    },

    availableCountries() {
      const seen = new Set();
      const countries = [];

      this.judges.forEach(judge => {
        const code = this.getJudgeCountryCode(judge);
        if (!code || seen.has(code)) {
          return;
        }
        seen.add(code);
        countries.push({
          code,
          flag: this.codeToFlag(code),
        });
      });

      return countries.sort((a, b) => a.code.localeCompare(b.code));
    }
  },
  async mounted() {
    await this.fetchJudges();
    if (this.appNavigation.judges?.currentPage) {
      this.currentPage = this.appNavigation.judges?.currentPage;
      this.searchQuery = this.appNavigation.judges?.searchQuery;
      this.selectedCountry = this.appNavigation.judges?.selectedCountry || "";
      this.selectedSort = this.appNavigation.judges?.selectedSort || "default";
      this.selectedCertification = this.appNavigation.judges?.selectedCertification || "all";
      this.clearAppNavigationZone("judges");
    }
    this.setupResponsivePagination();
  },
  beforeUnmount() {
    this.teardownResponsivePagination();
  },
  methods: {
    ...mapActions("competition",["updateAppNavigationZone","clearAppNavigationZone"]),
    setupResponsivePagination() {
      this.$nextTick(() => {
        this.scheduleItemsPerPageUpdate();
        window.addEventListener("resize", this.scheduleItemsPerPageUpdate);
        window.addEventListener("orientationchange", this.scheduleItemsPerPageUpdate);

        if (typeof ResizeObserver === "undefined") {
          return;
        }

        const target = this.$refs.boxComponent;
        if (!target) {
          return;
        }

        this.resizeObserver = new ResizeObserver((entries) => {
          const entry = entries?.[0];
          const nextWidth = Math.round(entry?.contentRect?.width || 0);

          if (!nextWidth || nextWidth === this.observedContainerWidth) {
            return;
          }

          this.observedContainerWidth = nextWidth;
          this.scheduleItemsPerPageUpdate();
        });
        this.resizeObserver.observe(target);
      });
    },
    teardownResponsivePagination() {
      window.removeEventListener("resize", this.scheduleItemsPerPageUpdate);
      window.removeEventListener("orientationchange", this.scheduleItemsPerPageUpdate);
      if (this.resizeFrame) {
        window.cancelAnimationFrame(this.resizeFrame);
        this.resizeFrame = null;
      }
      if (this.resizeObserver) {
        this.resizeObserver.disconnect();
        this.resizeObserver = null;
      }
    },
    scheduleItemsPerPageUpdate() {
      if (this.resizeFrame) {
        window.cancelAnimationFrame(this.resizeFrame);
      }

      this.resizeFrame = window.requestAnimationFrame(() => {
        this.resizeFrame = null;
        this.updateItemsPerPage();
      });
    },
    updateItemsPerPage() {
      const target = this.$refs.boxComponent;
      if (!target) {
        return;
      }

      const containerWidth = Math.round(target.clientWidth || 0);
      if (!containerWidth) {
        return;
      }

      this.observedContainerWidth = containerWidth;

      const nextItemsPerPage = resolveResponsiveGridItemsPerPage(containerWidth, window.innerWidth);
      if (nextItemsPerPage !== this.itemsPerPage) {
        this.itemsPerPage = nextItemsPerPage;
      }

      const totalPages = Math.max(1, Math.ceil(this.filteredJudges.length / this.itemsPerPage));
      if (this.currentPage > totalPages) {
        this.currentPage = totalPages;
      }
    },
    onFilterChange() {
      this.currentPage = 1;
    },
    resolveCompetitionsCount(judge) {
      const count = Number(judge?.stats?.competitions_count ?? 0);
      return Number.isFinite(count) ? count : 0;
    },
    resolveRoundsCount(judge) {
      const count = Number(judge?.stats?.rounds_scored_count ?? 0);
      return Number.isFinite(count) ? count : 0;
    },
    compareNumbers(left, right) {
      return left - right;
    },
    resolveJudgeCertificationType(judge) {
      const certification = judge?.certification;
      if (!certification) {
        return 'none';
      }

      const levelName = String(
        certification?.judge_level?.display_name || certification?.judge_level?.name || ''
      )
        .trim()
        .toLowerCase();

      if (levelName.includes('live')) {
        return 'live';
      }

      if (levelName.includes('online')) {
        return 'online';
      }

      return 'all';
    },
    getJudgeCountryCode(judge) {
      if (!judge) {
        return "";
      }
      const rawCode = judge.stats?.country_code || judge.country || "";
      return rawCode ? rawCode.toUpperCase() : "";
    },
    codeToFlag(code = "") {
      if (!code || code.length < 2) {
        return "";
      }
      const letters = code
          .toUpperCase()
          .replace(/[^A-Z]/g, "")
          .slice(0, 2);
      if (letters.length < 2) {
        return "";
      }
      return letters
          .split("")
          .map((char) => String.fromCodePoint(127397 + char.charCodeAt(0)))
          .join("");
    },
    async fetchJudges() {
      this.showLoading();
      try {
        const response = await this.$axios.get("/judges/list", {
          headers: {
            Authorization: `Bearer ${this.token}`,
          },
        });

        if (response.data?.judges && typeof response.data.judges === 'object') {
          // 🔁 Convertir objeto a array usando Object.values
          this.judges = Object.values(response.data.judges);
        } else {
          console.warn("⚠️ La respuesta no contiene atletas válidos.");
          this.judges = [];
        }
      } catch (error) {
        console.error("❌ Error al obtener las competiciones:", error);
      } finally {
        this.hideLoading();
      }
    },
    showLoading() {
      this.isLoading = true;
      this.$nextTick(() => {
        this.$refs.loadingModal.showLoading();
      });
    },
    hideLoading() {
      this.isLoading = false;
      this.$nextTick(() => {
        this.$refs.loadingModal.hideLoading();
      });
    },
    async handleViewJudge(judgeId) {
      this.updateAppNavigationZone({
        zone: "judges",
        data: {
          currentPage: this.currentPage,
          searchQuery: this.searchQuery,
          selectedCountry: this.selectedCountry,
          selectedSort: this.selectedSort,
          selectedCertification: this.selectedCertification,
        }
      });
      await router.push(`/profile/${judgeId}`);
    },

  },
};
</script>

<style scoped>
.box_component {
  width: 100%;
  max-width: 100%;
  margin: 15px auto;
}
.loading-text {
  text-align: center;
  font-size: 16px;
  color: #666;
}
.filter-bar {
  display: flex;
  gap: 10px;
  margin-bottom: 15px;
}
.search-input {
  flex: 1;
  padding: 8px;
  border: 1px solid #ccc;
  border-radius: 4px;
  max-width: 250px;
}
.header-title {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 15px;
}
.filter-bar {
  display: flex;
  gap: 10px;
  margin-bottom: 15px;
  flex-wrap: wrap;
}
.search-input{
  flex: 1;
  padding: 8px;
  border: 1px solid #ccc;
  border-radius: 4px;
}
.country-select {
  min-width: 160px;
  padding: 8px;
  border: 1px solid #ccc;
  border-radius: 4px;
  background-color: #fff;
}
.judge-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: 15px;
}
.judge-grid.single {
  justify-content: center;
}

.judge-grid.single .judge-card {
  max-width: 250px;
  width: 100%;
}
.judge-grid .judge-card {
  max-width: 200px;
  width: 100%;
}

.no-judges {
  text-align: center;
  font-size: 14px;
  color: #777;
}

.judges-header-top {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 12px;
}
.pagination-controls {
  display: flex;
  justify-content: center;
  align-items: center;
  gap: 12px;
  margin-top: 20px;
  flex-wrap: wrap;
}

.pagination-btn {
  background-color: #4c56af;
  color: white;
  border: none;
  padding: 8px 16px;
  border-radius: 6px;
  font-size: 14px;
  cursor: pointer;
  transition: background-color 0.2s ease;
}

.pagination-btn:hover:not(:disabled) {
  background-color: #3b4491;
}

.pagination-btn:disabled {
  background-color: #ccc;
  cursor: not-allowed;
}

.pagination-text {
  font-size: 14px;
  color: #444;
}

@media (max-width: 576px) {
  .judge-grid {
    grid-template-columns: repeat(auto-fit, minmax(150px, 2fr));
    gap: 2px;
  }
  .judge-grid .judge-card {
    max-width: 150px;
  }

  .judge-grid.single .judge-card {
    max-width: 100%;
  }
}

@media (max-width: 768px) {

}
</style>